'use client';

import { datadogRum } from '@datadog/browser-rum';
import throttle from 'lodash.throttle';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

const MILESTONES = [25, 50, 75, 100] as const;
const THROTTLE_MS = 150;
const PAGE_CONTENT_SELECTOR = '[data-subcomponent="page-content"]';

// The marketplace wraps every route in DS.Page, whose inner <main data-subcomponent="page-content">
// is the actual scroll container (overflow: overlay). Scroll events fire on that element and do
// not bubble — only a capture-phase listener on `document` catches them reliably.
const computeDepthPct = (scrollEl: Element) => {
  const scrollTop = Math.max(0, scrollEl.scrollTop);
  const viewport = scrollEl.clientHeight;
  const docHeight = scrollEl.scrollHeight;
  if (docHeight <= 0) return 0;
  return Math.min(100, Math.max(0, ((scrollTop + viewport) / docHeight) * 100));
};

export const useScrollDepth = () => {
  const pathname = usePathname();
  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    firedRef.current = new Set();
  }, [pathname]);

  useEffect(() => {
    const fireCrossedMilestones = (scrollEl: Element) => {
      const depthPct = computeDepthPct(scrollEl);
      MILESTONES.forEach((milestone) => {
        if (depthPct >= milestone && !firedRef.current.has(milestone)) {
          firedRef.current.add(milestone);
          datadogRum.addAction('scrollDepth', { depth_pct: milestone, page: window.location.pathname });
        }
      });
    };

    const onScroll = throttle(
      (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element) || !target.matches(PAGE_CONTENT_SELECTOR)) return;
        fireCrossedMilestones(target);
      },
      THROTTLE_MS,
      { leading: true, trailing: true }
    );

    // bf-cache restore starts a new RUM view, but firedRef survives — resync so the returning
    // user gets credit for the milestones their restored scroll position already satisfies.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      firedRef.current = new Set();
      const scrollEl = document.querySelector(PAGE_CONTENT_SELECTOR);
      if (!scrollEl) return;
      fireCrossedMilestones(scrollEl);
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('pageshow', onPageShow);
      onScroll.cancel();
    };
  }, []);
};
